import React from 'react';
import useAuth from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { trackAdClick } from '../../api/videos';
import { hasCompletedPaymentForAd } from '../../services/payment';

interface SocialShareButtonsProps {
  adId?: string;
  adTitle?: string;
}

interface AuthUserWithRole {
  role?: string;
  user?: {
    role?: string;
  };
}

export const SocialShareButtons: React.FC<SocialShareButtonsProps> = ({ adId, adTitle }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const isAdmin = (() => {
    if (!user) return false;
    const candidate = user as AuthUserWithRole;
    if (candidate.user && typeof candidate.user.role === 'string') {
      return candidate.user.role === 'admin';
    }
    if (typeof candidate.role === 'string') {
      return candidate.role === 'admin';
    }
    return false;
  })();

  const shareOnSocialMedia = async (platform: string) => {
    if (!isAdmin && adId) {
      const isPaid = await hasCompletedPaymentForAd(adId).catch(() => false);
      if (!isPaid) {
        navigate('/payment', {
          state: {
            fromCampaignWizard: true,
            adId,
            packageId: null,
            packageName: adTitle || null,
          },
        });
        return;
      }
    }

    const base =
      process.env.REACT_APP_SHARE_BASE ||
      process.env.REACT_APP_API_BASE ||
      (typeof window !== 'undefined' ? window.location.origin : '');

    const trackingUrl = adId
      ? `${base.replace(/\/$/, '')}/share/ad/${encodeURIComponent(adId)}?src=${encodeURIComponent(platform)}`
      : typeof window !== 'undefined'
        ? window.location.href
        : '';

    const text = adTitle ? `Check out this ad: ${adTitle}` : 'Check out this amazing ad!';

    let shareUrl = '';

    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(trackingUrl)}&quote=${encodeURIComponent(text)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(trackingUrl)}&text=${encodeURIComponent(text)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(trackingUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(text)} ${encodeURIComponent(trackingUrl)}`;
        break;
      case 'instagram':
        if (trackingUrl && typeof navigator !== 'undefined' && navigator.clipboard) {
          void navigator.clipboard.writeText(trackingUrl).catch(() => {
            // ignore clipboard errors
          });
        }
        shareUrl = 'https://www.instagram.com/';
        break;
      default:
        return;
    }

    window.open(shareUrl, '_blank');

    // Only track clicks when the current user is an admin
    if (isAdmin && adId) {
      void trackAdClick(adId);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        onClick={() => {
          void shareOnSocialMedia('facebook');
        }}
        className="inline-flex items-center rounded-full bg-[#1877F2]/10 px-3 py-1 text-xs font-medium text-[#1877F2] hover:bg-[#1877F2]/15"
      >
        Facebook
      </button>
      <button
        type="button"
        onClick={() => {
          void shareOnSocialMedia('twitter');
        }}
        className="inline-flex items-center rounded-full bg-sky-500/10 px-3 py-1 text-xs font-medium text-sky-500 hover:bg-sky-500/15"
      >
        X / Twitter
      </button>
      <button
        type="button"
        onClick={() => {
          void shareOnSocialMedia('linkedin');
        }}
        className="inline-flex items-center rounded-full bg-sky-700/10 px-3 py-1 text-xs font-medium text-sky-700 hover:bg-sky-700/15"
      >
        LinkedIn
      </button>
      <button
        type="button"
        onClick={() => {
          void shareOnSocialMedia('whatsapp');
        }}
        className="inline-flex items-center rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-600 hover:bg-emerald-500/15"
      >
        WhatsApp
      </button>
      <button
        type="button"
        onClick={() => {
          void shareOnSocialMedia('instagram');
        }}
        className="inline-flex items-center rounded-full bg-pink-500/10 px-3 py-1 text-xs font-medium text-pink-500 hover:bg-pink-500/15"
      >
        Instagram
      </button>
    </div>
  );
};

export default SocialShareButtons;
